import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/internal/operators/take';
import { ManagementUploadControllerService, ResourceRequest } from 'api-clipping';
import { RecordingService } from './recording.service';

export type RecordingUploadStatus = 'UPLOADING' | 'FINALIZING' | 'DONE' | 'ERROR';

export interface RecordingUploadState {
	id: string;
	mediumName: string;
	fileName: string;
	progress: number;
	status: RecordingUploadStatus;
	errorMessage?: string;
}

// Ж1: "Прикачи снимка" works like Клипинг's own clip-media uploads - the dialog closes as soon
// as the upload starts, and the upload itself continues here, in a root-provided service, so
// navigating away (or even closing the dialog) never cancels an in-flight HttpClient subscription
// or loses its progress state. The header icon (navbar.component) is just a read-only view onto
// `uploads` - this service is the only thing that writes to it. A window beforeunload guard is
// registered once, for the lifetime of the app, not per upload.
@Injectable({ providedIn: 'root' })
export class RecordingUploadService {
	uploads = new Map<string, RecordingUploadState>();

	constructor(
		private http: HttpClient,
		private apiUpload: ManagementUploadControllerService,
		private recordingService: RecordingService,
		private translate: TranslateService
	) {
		window.addEventListener('beforeunload', event => {
			if (this.hasActiveUploads) {
				event.preventDefault();
				event.returnValue = true;
			}
		});
	}

	get hasActiveUploads(): boolean {
		return Array.from(this.uploads.values()).some(u => u.status === 'UPLOADING' || u.status === 'FINALIZING');
	}

	get hasFailedUploads(): boolean {
		return Array.from(this.uploads.values()).some(u => u.status === 'ERROR');
	}

	dismiss(id: string): void {
		this.uploads.delete(id);
	}

	// З1 "Прикачи преку линк" - the server does the actual download (yt-dlp), so there is no
	// byte-level progress to show; the status stays FINALIZING (same label as "се обработува")
	// until the server responds.
	startFromLink(mediumId: string, mediumName: string, scheduledStart: string, sourceUrl: string): void {
		const id = `recording-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		const state: RecordingUploadState = { id, mediumName, fileName: sourceUrl, progress: 0, status: 'FINALIZING' };
		this.uploads.set(id, state);

		this.recordingService
			.uploadRecordingFromLink(mediumId, scheduledStart, sourceUrl)
			.pipe(take(1))
			.subscribe({
				next: () => {
					state.status = 'DONE';
					setTimeout(() => this.uploads.delete(id), 8000);
				},
				error: (err: HttpErrorResponse) => this.fail(state, this.describeFinalizeError(err)),
			});
	}

	start(mediumId: string, mediumName: string, scheduledStart: string, file: File): void {
		const id = `recording-upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
		const state: RecordingUploadState = { id, mediumName, fileName: file.name, progress: 0, status: 'UPLOADING' };
		this.uploads.set(id, state);

		this.apiUpload
			.generatePreSignedUrls([{ filename: file.name, contentType: file.type || 'video/mp4', type: ResourceRequest.TypeEnum.RECORDING, fileSize: file.size } as ResourceRequest])
			.pipe(take(1))
			.subscribe({
				next: response => this.putToB2(state, file, response[0]?.uploadUrl || '', response[0]?.cdnUrl || '', mediumId, scheduledStart),
				error: () => this.fail(state, this.translate.instant('RECORDINGS.UPLOAD.ERROR_PREPARE')),
			});
	}

	private putToB2(state: RecordingUploadState, file: File, uploadUrl: string, cdnUrl: string, mediumId: string, scheduledStart: string): void {
		if (!uploadUrl || !cdnUrl) {
			this.fail(state, this.translate.instant('RECORDINGS.UPLOAD.ERROR_PREPARE'));
			return;
		}
		this.http.put(uploadUrl, file, { headers: { 'Content-Type': file.type || 'video/mp4' }, reportProgress: true, observe: 'events' }).subscribe({
			next: event => {
				if (event.type === HttpEventType.UploadProgress && event.total) {
					state.progress = Math.round((100 * event.loaded) / event.total);
				} else if (event.type === HttpEventType.Response) {
					this.finalize(state, cdnUrl, mediumId, scheduledStart);
				}
			},
			error: (err: HttpErrorResponse) => this.fail(state, this.describeB2Error(err, state.progress)),
		});
	}

	private finalize(state: RecordingUploadState, cdnUrl: string, mediumId: string, scheduledStart: string): void {
		state.status = 'FINALIZING';
		this.recordingService
			.uploadRecording(mediumId, scheduledStart, cdnUrl)
			.pipe(take(1))
			.subscribe({
				next: () => {
					state.status = 'DONE';
					setTimeout(() => this.uploads.delete(state.id), 8000);
				},
				error: (err: HttpErrorResponse) => this.fail(state, this.describeFinalizeError(err)),
			});
	}

	private fail(state: RecordingUploadState, message: string): void {
		state.status = 'ERROR';
		state.errorMessage = message;
	}

	private describeB2Error(err: HttpErrorResponse, percent: number): string {
		if (err.status === 0) {
			return this.translate.instant('RECORDINGS.UPLOAD.ERROR_B2_NETWORK', { percent });
		}
		if (err.status === 403) {
			return this.translate.instant('RECORDINGS.UPLOAD.ERROR_B2_EXPIRED');
		}
		return this.translate.instant('RECORDINGS.UPLOAD.ERROR_B2_STATUS', { status: err.status, percent });
	}

	private describeFinalizeError(err: HttpErrorResponse): string {
		const serverMessage = err.error?.message;
		if (serverMessage) {
			return serverMessage;
		}
		if (err.status === 413) {
			return this.translate.instant('RECORDINGS.UPLOAD.ERROR_413');
		}
		if (err.status === 504) {
			return this.translate.instant('RECORDINGS.UPLOAD.ERROR_504');
		}
		return this.translate.instant('RECORDINGS.UPLOAD.ERROR_STATUS', { status: err.status });
	}
}
